import React, { useState, useEffect, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../Utilities/AuthContext";
import Modal from "./Modal";
import ContactUsForm from "./ContactUsForm";
import { FaTrophy, FaUsers } from "react-icons/fa";
import { IoGolfOutline, IoHomeOutline, IoPersonCircleOutline, IoChevronDown, IoLogOutOutline } from "react-icons/io5";
import { MdOutlineEmail, MdOutlineQuestionAnswer } from "react-icons/md";
import { logError } from "../Utilities/errorLogger";

function Navbar() {
  const location = useLocation();
  const navigate = useNavigate();
  const { logout, isInitialized, ownerData } = useAuth();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dropdownRef = useRef(null);
  const userName = ownerData?.userName || localStorage.getItem("userName") || "";

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      await logError({
        filePath: "ReusableComponents/Navbar.js",
        functionName: "handleLogout",
        errorType: "Logout Error",
        errorMessage: error.message,
        stackTrace: error.stack,
      });
    }
  };

  const isActive = (path) => location.pathname === path;

  if (!isInitialized) return null;

  const navItems = [
    { path: "/home", label: "Home", icon: <IoHomeOutline className="w-5 h-5" /> },
    { path: "/pools", label: "Pools", icon: <FaUsers className="w-5 h-5" /> },
    { path: "/teams", label: "Teams", icon: <IoGolfOutline className="w-5 h-5" /> },
    { path: "/leaderboard", label: "Boards", icon: <FaTrophy className="w-4 h-4" /> },
  ];

  return (
    <>
      {/* Desktop Navbar - Top */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 h-16 bg-primary text-white z-50 items-center justify-between px-6 shadow-lg">
        <Link to="/home" className="text-xl font-bold tracking-tight hover:text-green-200 transition-colors">
          GolfPools
        </Link>

        <div className="flex items-center gap-1">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                isActive(item.path)
                  ? "bg-white/20 text-white"
                  : "text-green-200 hover:bg-white/10 hover:text-white"
              }`}
            >
              {item.icon}
              {item.label}
            </Link>
          ))}
        </div>

        {/* User Menu */}
        <div className="relative" ref={dropdownRef}>
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-green-200 hover:bg-white/10 hover:text-white transition-all"
          >
            <IoPersonCircleOutline className="w-6 h-6" />
            <span className="max-w-[120px] truncate">{userName}</span>
            <IoChevronDown className={`w-4 h-4 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`} />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 top-full mt-1 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-1 overflow-hidden">
              <button
                onClick={() => { navigate("/faq"); setIsDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <MdOutlineQuestionAnswer className="w-4 h-4 text-gray-400" /> FAQs
              </button>
              <button
                onClick={() => { setIsModalOpen(true); setIsDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <MdOutlineEmail className="w-4 h-4 text-gray-400" /> Contact Us
              </button>
              <div className="border-t border-gray-100 my-1" />
              <button
                onClick={() => { handleLogout(); setIsDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                <IoLogOutOutline className="w-4 h-4" /> Sign Out
              </button>
            </div>
          )}
        </div>
      </nav>

      {/* Mobile Navbar - Bottom */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 pb-[env(safe-area-inset-bottom)]">
        <div className="flex items-center justify-around h-16">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${
                isActive(item.path)
                  ? "text-primary"
                  : "text-gray-400 hover:text-gray-600"
              }`}
            >
              {item.icon}
              <span className="text-[10px] font-medium">{item.label}</span>
            </Link>
          ))}
          <button
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            className={`flex flex-col items-center justify-center gap-0.5 px-3 py-1.5 rounded-lg transition-colors ${
              isDropdownOpen ? "text-primary" : "text-gray-400 hover:text-gray-600"
            }`}
          >
            <IoPersonCircleOutline className="w-5 h-5" />
            <span className="text-[10px] font-medium">Account</span>
          </button>
        </div>

        {/* Mobile dropdown menu */}
        {isDropdownOpen && (
          <>
            <div className="fixed inset-0 bg-black/30 z-40" onClick={() => setIsDropdownOpen(false)} />
            <div className="absolute bottom-full left-0 right-0 bg-white rounded-t-2xl shadow-xl border-t border-gray-200 z-50 py-2">
              <div className="px-4 py-3 border-b border-gray-100">
                <p className="text-sm font-semibold text-gray-900">{userName}</p>
              </div>
              <button
                onClick={() => { navigate("/faq"); setIsDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
              >
                <MdOutlineQuestionAnswer className="w-5 h-5 text-gray-400" /> FAQs
              </button>
              <button
                onClick={() => { setIsModalOpen(true); setIsDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-gray-700 hover:bg-gray-50"
              >
                <MdOutlineEmail className="w-5 h-5 text-gray-400" /> Contact Us
              </button>
              <button
                onClick={() => { handleLogout(); setIsDropdownOpen(false); }}
                className="w-full flex items-center gap-3 px-4 py-3 text-sm text-red-600 hover:bg-red-50"
              >
                <IoLogOutOutline className="w-5 h-5" /> Sign Out
              </button>
            </div>
          </>
        )}
      </nav>

      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} isSubmitting={isSubmitting} contentType="modal">
        <ContactUsForm closeModal={() => setIsModalOpen(false)} onSubmissionStatusChange={setIsSubmitting} />
      </Modal>
    </>
  );
}

export default Navbar;
